import api from '~/services/api';
import { getSubscriberIdFromToken } from '~/util/checkout';

/**
 * Serviço de telemetria para diagnóstico de falhas no fluxo 3DS.
 *
 * Endpoint:
 *   POST /shareable-carts/debug-email/{cartId}/threeds/debug
 *   Header: X-Shareable-Token
 */

const STAGE_LABELS = {
  token: 'Obtenção do token 3DS',
  sdk_load: 'Carregamento do SDK Stone',
  containers: 'Containers DOM não encontrados',
  authenticate: 'Autenticação 3DS (handle3DSAuthenticationV2)',
  status_eval: 'Avaliação de status 3DS',
  finalize: 'Finalização do pedido',
  preview: 'Preview do antifraude',
};

function maskPan(pan) {
  const digits = String(pan || '').replace(/\D/g, '');
  if (digits.length < 8) return '****';
  return `${digits.slice(0, 4)} **** **** ${digits.slice(-4)}`;
}

function extractErrorInfo(err) {
  if (!err) return { message: 'Erro desconhecido (null/undefined)' };

  const info = {
    message: err?.message || String(err),
    name: err?.name || null,
    code: err?.code || null,
    stack: err?.stack ? err.stack.split('\n').slice(0, 8).join('\n') : null,
  };

  if (err?.response) {
    info.http_status = err.response.status;
    info.http_data = err.response.data;
  }

  return info;
}

/**
 * Envia telemetria de erro 3DS para o backend.
 * Silencia erros para nunca quebrar o fluxo do checkout.
 *
 * @param {Object} params
 * @param {string} params.token - X-Shareable-Token
 * @param {string} params.cartId - ID do carrinho
 * @param {string} params.stage - Etapa do fluxo (token|authenticate|status_eval|finalize)
 * @param {Object} params.error - Objeto de erro capturado
 * @param {Object} [params.context] - storeId, storeName, gateway, brand, cardNumberRaw
 * @param {Object} [params.threedsData] - tdsServerTransId, transStatus, orderData3ds, tdsResult
 * @param {Object} [params.extra] - Qualquer dado extra relevante
 */
export async function sendThreeDSDebug({
  token,
  cartId,
  stage,
  error,
  context = {},
  threedsData = {},
  extra = null,
}) {
  try {
    const errorInfo = extractErrorInfo(error);
    const subscriberId = getSubscriberIdFromToken(token);

    const orderData = threedsData.orderData3ds;
    const orderSummary = orderData
      ? {
          customer_name: orderData?.customer?.name,
          customer_email: orderData?.customer?.email,
          payment_amount: orderData?.payments?.[0]?.amount,
          items_count: orderData?.items?.length,
          billing_address:
            orderData?.payments?.[0]?.credit_card?.card?.billing_address,
          shipping_address: orderData?.shipping?.address,
        }
      : null;

    const payload = {
      // Identificação
      stage,
      stage_label: STAGE_LABELS[stage] || stage,
      subscriber_id: subscriberId || null,
      cart_id: cartId,
      store_id: context.storeId || null,
      store_name: context.storeName || null,
      gateway: context.gateway || null,

      // Cartão (mascarado)
      brand: context.brand || null,
      card_masked: maskPan(context.cardNumberRaw),

      // 3DS
      tds_server_trans_id: threedsData.tdsServerTransId || null,
      trans_status: threedsData.transStatus || null,
      threeds_order_data: orderSummary,

      // Erro
      error: errorInfo,

      // SDK Stone
      sdk_status: {
        tds_loaded: typeof window.TDS !== 'undefined',
        script_tag_exists: !!document.getElementById('stone-3ds-script'),
        loader_state: window.__stone3ds || null,
      },

      // Browser
      user_agent: navigator.userAgent,
      screen: `${window.screen.width}x${window.screen.height}`,
      url: window.location.href,
      timestamp: new Date().toISOString(),

      // Extra
      extra: extra || null,
    };

    await api.post(
      `/shareable-carts/debug-email/${cartId}/threeds/debug`,
      payload,
      {
        headers: { 'X-Shareable-Token': token },
        timeout: 10000,
      }
    );
  } catch (debugErr) {
    console.warn('[3DS Debug] Falha ao enviar telemetria:', debugErr?.message);
  }
}

export default sendThreeDSDebug;
